import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Icon } from '@mui/material';
import { FakeText, Typography } from '@totalsoft/rocket-ui';
import { FULL_GENERIC_FILTER_TEMPLATE } from 'features/genericFilterTemplates/edit/queries';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { UPDATE_INSURANCE_CONVENTION_CONDITIONS } from 'features/insuranceConventions/edit/mutations';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { INSURANCE_CONVENTION_CONDITIONS_UPDATED } from 'features/insuranceConventions/edit/subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import useGenericFilterData, { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionConditionsContainer = ({ productId, templateId }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const isNewConvention = useMemo(() => !productId, [productId]);

  const [loaded, setLoaded] = useState(false);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultTemplate);

  // #region Data fetching

  const { templates, parameters, operators, loadValues, loadPagedValues, loadMissingParamters, loadTemplate } = useGenericFilterData({
    context: GenericFilterContext.InsuranceConvention,
    withTemplateSelection: true
  });

  const { loading } = useQuery(FULL_GENERIC_FILTER_TEMPLATE, {
    skip: !templateId,
    variables: { id: templateId },
    onCompleted: data => {
      const generalData = data.genericFilterTemplateGeneralData;
      const templateData = {
        withTemplate: Boolean(generalData.isTemplate),
        templateId: generalData.id,
        template: {
          phrase: data.genericFilterTemplateDetails.phrase,
          conditions: data.genericFilterTemplateDetails.conditions || []
        }
      };
      if (templateData.withTemplate) loadMissingParamters(templateData.template.conditions);
      resetLens(templateData);
      setInitialData(templateData);
      setLoaded(true);
    }
  });

  // #endregion

  // #region Side effects

  useEffect(() => {
    resetLens(prev => ({ ...prev, disabled: !canWrite }));
  }, [canWrite, resetLens]);

  // #endregion

  // #region Save

  const [mutateConditions] = useMutation(UPDATE_INSURANCE_CONVENTION_CONDITIONS, {
    onError: _ => {
      (lens.disabled |> set)(false);
    }
  });

  const handleSave = data => {
    (lens.disabled |> set)(true);

    const input = { productId, ...data };

    mutateConditions({ variables: { input } });
  };

  useSubscription(INSURANCE_CONVENTION_CONDITIONS_UPDATED, {
    skip: !productId,
    variables: { productId },
    onData: ({ client }) => {
      const currentData = lens |> get;
      const newData = {
        ...currentData,
        disabled: false,
        template: { ...currentData.template, conditions: currentData.template.conditions.map(x => ({ ...x, editing: false })) }
      };
      resetLens(newData);
      setInitialData(newData);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterTemplateDetails' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterTemplateGeneralData' });
      addToast(t('InsuranceConventions.Details.Conditions.ConditionsSaved'), 'success');
    }
  });

  // #endregion

  const showLoading = useMemo(() => loading && !loaded, [loading, loaded]);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <Grid container flexDirection='column' gap={2} p={2}>
      {isNewConvention && (
        <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
          <Icon fontSize='small' color='primary'>
            error
          </Icon>
          <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
        </Box>
      )}
      {!isNewConvention && showLoading && (
        <Grid item>
          <FakeText lines={5} />
        </Grid>
      )}
      {!isNewConvention && !showLoading && (
        <GenericFilterComposer
          lens={lens}
          dirtyInfo={dirtyInfo}
          resetLens={resetLens}
          templates={templates}
          parameters={parameters}
          operators={operators}
          loadValues={loadValues}
          loadPagedValues={loadPagedValues}
          loadTemplate={loadTemplate}
          onSave={handleSave}
          conditionsViewMode='table'
          withTemplateGeneralData
          withTemplateSelection
          forwardProps={{ container: { mt: -1 } }}
        />
      )}
    </Grid>
  );
};

InsuranceConventionConditionsContainer.propTypes = {
  productId: PropTypes.number,
  templateId: PropTypes.number
};

export default InsuranceConventionConditionsContainer;
